// vendors
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
// libs
import { CellExportFacade, NotebookTypes } from '@mode/notebooks/contract';
import { NotebookResource } from './notebook.resource';
import { NormalizedNotebookCellRunResult } from './notebook.types';
import { NotebookState } from '@mode/notebooks/contract';
import * as NotebookCellSelectors from './notebook-cell/legacy-notebook-cell.selectors';
import * as NotebookSelectors from './notebook/legacy-notebook.selectors';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

@Injectable()
export class NotebookStateService {
  selectedNotebookToken$: Observable<string>;
  notebookState$: Observable<NotebookState>;
  notebookResource$: Observable<NotebookResource>;

  canRestartSession$: Observable<boolean>;
  notebookType$: Observable<NotebookTypes.NotebookType>;

  cellExecuting$: Observable<boolean>;
  contentType$: Observable<NotebookTypes.ContentType>;
  isInLayout$: Observable<boolean>;
  isEditing$: Observable<boolean>;
  isFrozen$: Observable<boolean>;
  isFolded$: Observable<boolean>;
  cellToken$: Observable<string>;
  nextCellToken$: Observable<string>;
  prevCellToken$: Observable<string>;

  canFreeze$: Observable<boolean>;
  canDelete$: Observable<boolean>;
  isSelectedCellExported$: Observable<boolean>;
  isExportInProgress$: Observable<boolean>;

  hasResult$: Observable<boolean>;

  constructor(
    private store: Store,
    private cellExportFacade: CellExportFacade
  ) {
    this.selectedNotebookToken$ = this.store.select(NotebookSelectors.selectedNotebookToken);
    this.notebookState$ = this.selectedNotebookToken$.pipe(
      switchMap((token) =>
        this.store.select(NotebookSelectors.statusBarStateByNotebook, token).pipe(distinctUntilChanged())
      )
    );

    this.notebookResource$ = this.store
      .select(NotebookSelectors.selectedNotebookResource)
      .pipe(filter((r) => r !== null));
    this.notebookType$ = this.store.select(NotebookSelectors.selectedNotebookType);

    this.cellToken$ = this.store.select(NotebookSelectors.selectedCellToken);
    this.cellExecuting$ = this.store.select(NotebookCellSelectors.selectedCellExecuting);
    this.contentType$ = this.store.select(NotebookSelectors.selectedCellContentType);
    this.nextCellToken$ = this.store.select(NotebookCellSelectors.selectedNextCellToken);
    this.prevCellToken$ = this.store.select(NotebookCellSelectors.selectedPrevCellToken);

    this.isEditing$ = this.store.select(NotebookCellSelectors.isEditing);
    this.isFrozen$ = this.store.select(NotebookCellSelectors.selectedCellFrozen);
    this.isFolded$ = this.store.select(NotebookCellSelectors.selectedCellFolded);
    this.isInLayout$ = this.store.select(NotebookCellSelectors.selectedCellIsInLayout);

    this.canRestartSession$ = this.notebookState$.pipe(map((s) => s !== NotebookState.Session));
    this.canFreeze$ = this.cellToken$.pipe(
      switchMap((cellToken) => this.store.select(NotebookCellSelectors.canFreezeByCell, cellToken))
    );
    this.canDelete$ = combineLatest([
      this.cellToken$,
      this.cellExecuting$,
      this.cellExportFacade.exportingCellToken$,
    ]).pipe(
      map(([token, executing, exportingCellToken]) => {
        return token !== null && !executing && exportingCellToken !== token;
      })
    );
    this.hasResult$ = this.store.select(NotebookCellSelectors.selectedCellHasResult());

    this.isSelectedCellExported$ = this.cellToken$.pipe(
      switchMap((token) => this.store.select(NotebookCellSelectors.isExportedByCell, token) as Observable<boolean>)
    );
    this.isExportInProgress$ = this.cellExportFacade.isExporting$;
  }

  canRunCell$ = (notebookToken: any) =>
    this.store.select(NotebookCellSelectors.createCanCellRunSelector(notebookToken));

  isCellEditing$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createIsCellEditingSelector(cellToken));

  isMarkdownViewMode$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createMarkdownViewModeSelector(cellToken));

  createCellExecutionCountSelector$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createCellExecutionCountSelector(cellToken));

  createCellAceLangSelector$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createCellAceLangSelector(cellToken));

  getIsCellExported$(cellToken: string): Observable<boolean> {
    return this.store.select(NotebookCellSelectors.isExportedByCell, cellToken);
  }

  createLastResultsByCellSelector(cellToken: string): Observable<NormalizedNotebookCellRunResult[]> {
    return this.store.select(NotebookCellSelectors.createLastResultsByCellSelector(cellToken));
  }
}
